import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Check, X, ArrowUpRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

/**
 * PlaybookProposalsPanel — surfaces every un-applied playbook_item proposal
 * that targets this engagement, aggregated across all linked briefings.
 *
 * Rendered at the top of the engagement's ConveningPlaybook so partners
 * accept proposals in-context without leaving the playbook view. Accepting
 * calls the same `/notes/{id}/briefing/apply` endpoint that Briefing Studio
 * uses, so provenance + audit trail stay consistent (each accepted item
 * lands in the playbook with a `source_briefing_id` reference).
 */
export default function PlaybookProposalsPanel({ engagement, onApplied }) {
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    if (!engagement?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/engagements/${engagement.id}/playbook-proposals`);
      setGroups(data.groups || {});
    } catch (e) {
      // Silent — no proposals is a common state, not an error worth toasting.
      setGroups({});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [engagement?.id]);

  const totalCount = useMemo(
    () => Object.values(groups).reduce((sum, arr) => sum + arr.length, 0),
    [groups],
  );

  if (loading && totalCount === 0) return null;
  if (totalCount === 0) return null;

  const accept = async (p) => {
    setBusyId(p.proposal_id);
    try {
      const { data } = await api.post(`/notes/${p.source_briefing_id}/briefing/apply`, {
        proposal_ids: [p.proposal_id],
      });
      const result = data.results?.[p.proposal_id];
      if (result?.ok) {
        toast.success("Playbook prompt accepted");
        await load();
        onApplied?.();
      } else {
        toast.error(result?.error || "Accept failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (p) => {
    setBusyId(p.proposal_id);
    try {
      await api.patch(`/notes/${p.source_briefing_id}/briefing/proposals/${p.proposal_id}`, {
        status: "rejected",
      });
      toast.success("Proposal rejected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="border border-brass/50 bg-brass-soft/40 p-5 mb-6"
      data-testid="playbook-proposals-panel"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-brass">
          <Sparkle size={14} weight="fill" />
          <div className="text-[0.625rem] font-mono uppercase tracking-widest">
            Proposals from briefings · {totalCount} awaiting review
          </div>
        </div>
        <div className="text-[0.6875rem] text-muted-foreground">
          Accepting adds a prompt to the playbook section below. Rejecting keeps a trace on the source briefing.
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(groups).map(([sectionKey, items]) => (
          <div key={sectionKey} className="space-y-2">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">
              {sectionKey.replace(/_/g, " ")} · {items.length}
            </div>
            {items.map((p) => {
              const prompt = typeof p.value === "object" ? p.value?.prompt : String(p.value || "");
              return (
                <div
                  key={p.proposal_id}
                  data-testid={`playbook-proposal-${p.proposal_id}`}
                  className="border border-border bg-card p-3 space-y-2"
                >
                  <div className="text-sm leading-relaxed">{prompt}</div>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Link
                      to={`/notes#note-${p.source_briefing_id}`}
                      className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                      data-testid={`playbook-proposal-source-${p.proposal_id}`}
                    >
                      <ArrowUpRight size={10} weight="bold" /> From briefing: {p.source_briefing_title}
                    </Link>
                    <div className="flex gap-1.5">
                      <Button
                        data-testid={`playbook-proposal-accept-${p.proposal_id}`}
                        onClick={() => accept(p)}
                        disabled={busyId === p.proposal_id}
                        size="sm"
                        className="h-7 rounded-none bg-primary text-primary-foreground text-[0.6875rem]"
                      >
                        <Check size={11} weight="bold" className="mr-1" /> Accept into playbook
                      </Button>
                      <Button
                        data-testid={`playbook-proposal-reject-${p.proposal_id}`}
                        onClick={() => reject(p)}
                        disabled={busyId === p.proposal_id}
                        variant="outline"
                        size="sm"
                        className="h-7 rounded-none text-[0.6875rem] border-destructive/60 text-destructive hover:bg-destructive/10"
                      >
                        <X size={11} weight="bold" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
